/** Driving routes + ETA via Google Directions (road network, not straight line). */

import { importLibrary } from '@googlemaps/js-api-loader';
import { loadGoogleMaps } from '@/utils/loadGoogleMaps';

const ROUTE_CACHE_MS = 45_000;
const routeCache = new Map();

const DASHED_ROUTE_ICONS = [
  {
    icon: {
      path: 'M 0,-1 0,1',
      strokeOpacity: 1,
      scale: 3,
    },
    offset: '0',
    repeat: '14px',
  },
];

function routeCacheKey(from, to) {
  const r = (n) => Number(n).toFixed(4);
  return `${r(from.lat)},${r(from.lng)}|${r(to.lat)},${r(to.lng)}`;
}

function toLatLngLiteral(point) {
  if (!point) return null;
  if (typeof point.lat === 'function' && typeof point.lng === 'function') {
    return { lat: point.lat(), lng: point.lng() };
  }
  const lat = Number(point.lat);
  const lng = Number(point.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return { lat, lng };
  }
  return null;
}

function getEncodedPolyline(poly) {
  if (typeof poly === 'string' && poly.trim()) return poly.trim();
  if (poly && typeof poly.points === 'string' && poly.points.trim()) {
    return poly.points.trim();
  }
  return '';
}

/** Build a dense road-following path from a Directions API result. */
export function extractPathFromDirections(result) {
  const route = result?.routes?.[0];
  if (!route) return [];

  const encoding = window.google?.maps?.geometry?.encoding;
  const encoded = getEncodedPolyline(route.overview_polyline);
  if (encoded && encoding?.decodePath) {
    try {
      const decoded = encoding.decodePath(encoded);
      if (decoded?.length >= 2) {
        return decoded.map(toLatLngLiteral).filter(Boolean);
      }
    } catch (_) {
      /* fall through */
    }
  }

  if (Array.isArray(route.overview_path) && route.overview_path.length >= 2) {
    return route.overview_path.map(toLatLngLiteral).filter(Boolean);
  }

  const path = [];
  for (const leg of route.legs || []) {
    for (const step of leg.steps || []) {
      const stepEncoded = step.encoded_lat_lngs || getEncodedPolyline(step.polyline);
      if (stepEncoded && encoding?.decodePath) {
        try {
          const decoded = encoding.decodePath(stepEncoded);
          for (const p of decoded) {
            const ll = toLatLngLiteral(p);
            if (ll) path.push(ll);
          }
          continue;
        } catch (_) {
          /* use step.path below */
        }
      }
      for (const p of step.path || []) {
        const ll = toLatLngLiteral(p);
        if (ll) path.push(ll);
      }
    }
  }
  return path;
}

async function ensureDirectionsApi() {
  await loadGoogleMaps().catch(() => {});
  try {
    await importLibrary('geometry');
  } catch (_) {
    /* optional for polyline decode */
  }
  try {
    await importLibrary('routes');
  } catch (_) {
    /* Directions may already be on google.maps */
  }

  const DirectionsServiceCtor =
    window.google?.maps?.DirectionsService ||
    (await importLibrary('routes').catch(() => null))?.DirectionsService;

  if (!DirectionsServiceCtor) return null;
  return new DirectionsServiceCtor();
}

function requestDrivingEtaMatrix({ from, to }) {
  if (!window.google?.maps?.DistanceMatrixService) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const service = new window.google.maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [from],
        destinations: [to],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
      },
      (response, status) => {
        const element = response?.rows?.[0]?.elements?.[0];
        if (status === 'OK' && element?.status === 'OK') {
          resolve({
            durationText: element.duration?.text,
            durationValue: element.duration?.value,
            distanceText: element.distance?.text,
            distanceValue: element.distance?.value,
          });
          return;
        }
        resolve(null);
      },
    );
  });
}

/**
 * Request a driving route between two lat/lng points.
 * Never returns a 2-point straight line as a "path" — that misleads users.
 * @returns {Promise<{ path, directionsResult?, durationText?, durationValue?, distanceText?, distanceValue?, fallback: boolean }>}
 */
export async function requestDrivingRoute({ from, to }) {
  if (!from || !to) {
    return { path: [], fallback: true };
  }

  const key = routeCacheKey(from, to);
  const cached = routeCache.get(key);
  if (cached && Date.now() - cached.ts < ROUTE_CACHE_MS) {
    return cached;
  }

  const directionsService = await ensureDirectionsApi();
  if (!directionsService) {
    const matrix = await requestDrivingEtaMatrix({ from, to });
    return {
      path: [],
      fallback: true,
      ts: Date.now(),
      ...matrix,
    };
  }

  const travelMode =
    window.google?.maps?.TravelMode?.DRIVING || 'DRIVING';

  const directionsResult = await new Promise((resolve) => {
    directionsService.route(
      {
        origin: from,
        destination: to,
        travelMode,
        provideRouteAlternatives: false,
      },
      (result, status) => {
        if (status === 'OK' && result?.routes?.[0]) {
          resolve(result);
          return;
        }
        console.warn('[drivingRoute] Directions failed:', status, from, to);
        resolve(null);
      },
    );
  });

  if (directionsResult) {
    const route = directionsResult.routes[0];
    const leg = route.legs?.[0];
    const path = extractPathFromDirections(directionsResult);
    const payload = {
      directionsResult,
      // Only keep multi-point road paths — never a fake A→B straight segment.
      path: path.length > 2 ? path : [],
      durationText: leg?.duration?.text,
      durationValue: leg?.duration?.value,
      distanceText: leg?.distance?.text,
      distanceValue: leg?.distance?.value,
      fallback: path.length <= 2,
      ts: Date.now(),
    };
    routeCache.set(key, payload);
    return payload;
  }

  const matrix = await requestDrivingEtaMatrix({ from, to });
  return {
    path: [],
    fallback: true,
    ts: Date.now(),
    ...matrix,
  };
}

function clearRouteOverlay(overlay) {
  if (!overlay) return;
  if (typeof overlay.setMap === 'function') {
    overlay.setMap(null);
  }
}

/**
 * Draw a road-following dashed polyline (preferred).
 * Path must already be a Directions-decoded road path (many points).
 */
export function addDrivingRoutePolyline({ map, path, overlaysRef }) {
  if (!map || !Array.isArray(path) || path.length <= 2 || !window.google?.maps) return null;

  const line = new window.google.maps.Polyline({
    path,
    geodesic: false,
    strokeColor: '#2563eb',
    strokeOpacity: 0.9,
    strokeWeight: 4,
    map,
    zIndex: 40,
  });

  // Overlay a dashed pattern on top for the legend style
  const dashed = new window.google.maps.Polyline({
    path,
    geodesic: false,
    strokeColor: '#1d4ed8',
    strokeOpacity: 0,
    strokeWeight: 0,
    icons: DASHED_ROUTE_ICONS.map((item) => ({
      ...item,
      icon: {
        ...item.icon,
        strokeColor: '#1d4ed8',
      },
    })),
    map,
    zIndex: 41,
  });

  if (overlaysRef?.current) {
    overlaysRef.current.push(line, dashed);
  }
  return line;
}

/** Draw route with Google's DirectionsRenderer (follows roads). */
export function addDrivingRouteRenderer({ map, directionsResult, routeOverlaysRef }) {
  if (!map || !directionsResult || !window.google?.maps?.DirectionsRenderer) return null;

  const renderer = new window.google.maps.DirectionsRenderer({
    map,
    directions: directionsResult,
    suppressMarkers: true,
    preserveViewport: true,
    polylineOptions: {
      strokeColor: '#2563eb',
      strokeOpacity: 0.9,
      strokeWeight: 4,
      zIndex: 40,
    },
  });

  if (routeOverlaysRef?.current) routeOverlaysRef.current.push(renderer);
  return renderer;
}

/**
 * Draw road-following routes for all active services (vehicle → destination).
 * @returns {Promise<Record<string, { durationText, durationValue, distanceText, distanceValue, fallback }>>}
 */
export async function updateDrivingRoutesOnMap({
  map,
  activeServices,
  routeOverlaysRef,
  getCurrentGps,
  getDeliveryGps,
}) {
  const etaByServiceId = {};
  if (!map || !Array.isArray(activeServices) || activeServices.length === 0) {
    return etaByServiceId;
  }

  await loadGoogleMaps().catch(() => {});

  if (routeOverlaysRef?.current) {
    routeOverlaysRef.current.forEach(clearRouteOverlay);
    routeOverlaysRef.current = [];
  }

  const tasks = activeServices.map(async (service) => {
    const cur = getCurrentGps(service);
    const delivery = getDeliveryGps(service);
    if (!cur || !delivery) return;

    const from = { lat: cur.lat, lng: cur.lng };
    const to = { lat: delivery.lat, lng: delivery.lng };
    const route = await requestDrivingRoute({ from, to });

    // Prefer decoded road polyline (many points). Never draw a 2-point straight line.
    if (route.path?.length > 2) {
      addDrivingRoutePolyline({ map, path: route.path, overlaysRef: routeOverlaysRef });
    } else if (route.directionsResult && window.google?.maps?.DirectionsRenderer) {
      addDrivingRouteRenderer({
        map,
        directionsResult: route.directionsResult,
        routeOverlaysRef,
      });
    }

    if (service.id) {
      etaByServiceId[service.id] = {
        durationText: route.durationText,
        durationValue: route.durationValue,
        distanceText: route.distanceText,
        distanceValue: route.distanceValue,
        fallback: route.fallback,
      };
    }
  });

  await Promise.all(tasks);
  return etaByServiceId;
}
